/**
 * v3.19-1421 · 1PM UPDATE FINALIZATION — Onboarding Call Workspace
 *
 * Section order (spec §1):
 *   1. Quick Reference          (left column, sticky on desktop)
 *   2. Ghost Assistant          (right col top — most-used first)
 *   3. Live Call Notes          (right col bottom, high-visibility surface)
 *
 * Quick Actions (spec §3):  Create Task · Create Appointment
 *   Both use the existing CRM endpoints (POST /tasks and the existing
 *   ScheduleAppointmentModal) — NO duplicate implementations.  Modal
 *   stays open while the rep files either action.
 *
 * Ghost integration (spec §2):
 *   The /ghost/query endpoint already hydrates the full client context
 *   from `active_client_id` (client, deal, timeline, activities, emails,
 *   texts, notes, loan, offers, workflow, stage, team).  This UI now
 *   surfaces that with a visible "Ghost is connected to" strip and
 *   accepts free-form questions with quick prompts as OPTIONAL helpers.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import {
  ClipboardList, Send, MessageCircle, Loader2, CheckCircle2, User, Building2,
  Briefcase, HandCoins, DollarSign, Calendar, Mail, FileWarning, StickyNote,
  Sparkles, ListTodo, CalendarPlus,
} from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';
import ScheduleAppointmentModal from './ScheduleAppointmentModal';
import { useAuth } from '../contexts/AuthContext';

// -------------------------------------------------------------------
// Session cache — survives modal close/reopen for the same clientId+callSid.
// -------------------------------------------------------------------
const SESSION_CACHE = new Map();
const sessionKey = (clientId, callSid) => `${clientId || 'nocli'}::${callSid || 'nocall'}`;
function getSession(cid, sid) {
  const k = sessionKey(cid, sid);
  if (!SESSION_CACHE.has(k)) {
    SESSION_CACHE.set(k, { draft: '', saved: [], ghost: [] });
  }
  return SESSION_CACHE.get(k);
}

const money = (v) =>
  (v == null || v === '') ? '—'
  : `$${Number(v).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

const relTime = (iso) => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (s < 60)   return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
};

const isTouchDevice = () => {
  if (typeof window === 'undefined') return false;
  return ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);
};

// -------------------------------------------------------------------
// Quick Reference — read-only snapshot.
// -------------------------------------------------------------------
const QRRow = ({ Icon, label, value, tone = 'slate' }) => (
  <div className="flex items-start gap-2 min-w-0" data-testid={`qr-${label.toLowerCase().replace(/\s+/g,'-')}`}>
    <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 text-${tone}-500`} />
    <div className="min-w-0 flex-1">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">{label}</div>
      <div className="text-sm text-slate-900 font-medium truncate">{value ?? '—'}</div>
    </div>
  </div>
);

function QuickReference({ ctx, loading }) {
  if (loading) return (
    <div className="text-xs text-slate-500 flex items-center gap-2">
      <Loader2 className="h-3 w-3 animate-spin" /> Loading snapshot…
    </div>
  );
  if (!ctx) return <div className="text-xs text-slate-500">No context</div>;
  return (
    <div className="space-y-2" data-testid="onboarding-quick-reference">
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        <QRRow Icon={User}          label="Client"     value={ctx.client_name} />
        <QRRow Icon={Building2}     label="Company"    value={ctx.company} />
        <QRRow Icon={ClipboardList} label="Stage"      value={ctx.deal_stage_label} />
        <QRRow Icon={Briefcase}     label="Rep"        value={ctx.sales_rep} />
        <QRRow Icon={User}          label="Manager"    value={ctx.manager} />
        <QRRow Icon={HandCoins}     label="Concierge"  value={ctx.application_concierge} />
        <QRRow Icon={DollarSign}    label="Requested"  value={money(ctx.loan_requested)} />
        <QRRow Icon={CheckCircle2}  label="Approved"   value={money(ctx.loan_approved)} tone="emerald" />
        <QRRow Icon={DollarSign}    label="Monthly"    value={money(ctx.monthly_payment)} />
        <QRRow Icon={Calendar}      label="Term"       value={ctx.loan_term ? `${ctx.loan_term} mo` : '—'} />
      </div>
      <div className="grid grid-cols-1 gap-2 pt-1 border-t border-slate-100">
        <div className="flex items-start gap-2">
          <Mail className="h-3.5 w-3.5 mt-0.5 shrink-0 text-slate-500" />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">Last Email</div>
            {ctx.last_email ? (
              <div className="text-xs text-slate-900" data-testid="qr-last-email">
                <div className="truncate font-medium">{ctx.last_email.subject}</div>
                <div className="text-slate-500 text-[11px] truncate">
                  {ctx.last_email.at_et} · {ctx.last_email.sender} · {relTime(ctx.last_email.at)}
                </div>
              </div>
            ) : <div className="text-xs text-slate-500">No emails on record</div>}
          </div>
        </div>
        <div className="flex items-start gap-2">
          <FileWarning className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${ctx.missing_documents?.length ? 'text-amber-600' : 'text-emerald-600'}`} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold leading-tight">Missing Documents</div>
            {ctx.missing_documents?.length
              ? (
                <div className="flex flex-wrap gap-1 mt-0.5" data-testid="qr-missing-docs">
                  {ctx.missing_documents.map((d) => (
                    <span key={d} className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-semibold">{d}</span>
                  ))}
                </div>
              )
              : <div className="text-xs text-emerald-700 font-medium" data-testid="qr-no-missing-docs">None outstanding</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Ghost Chat — free-form, context-aware
// -------------------------------------------------------------------
function GhostChat({ clientId, clientName, session, pushHistory }) {
  const [q, setQ] = useState('');
  const [asking, setAsking] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [session.ghost.length, asking]);

  const ask = async (overrideText) => {
    const query = (overrideText != null ? overrideText : q).trim();
    if (!query || asking) return;
    setAsking(true);
    pushHistory({ role: 'user', text: query });
    if (!overrideText) setQ('');
    try {
      // v3.21-1630 · Onboarding modal routes through unified Ghost.
      const res = await api.post('/ghost/answer', {
        query,
        capability:       'onboarding',
        active_client_id: clientId,
      });
      const d = res.data || {};
      const body   = d.body || d.formatted_response || d.message || 'No answer available.';
      const source = d.source || 'suggested_wording';
      const badge  = source === 'approved_script'       ? 'Approved Script'
                   : source === 'operational_knowledge' ? 'Approved Guidance'
                   : source === 'data'                  ? 'Hub Data'
                   : source === 'query_help'            ? 'Ghost Help'
                   :                                       'Suggested Wording — review before using';
      pushHistory({ role: 'ghost', text: `[${badge}]\n${body}` });
    } catch (e) {
      pushHistory({ role: 'ghost', text: `Ghost unavailable: ${e?.response?.data?.detail || e?.message || 'unknown error'}` });
    } finally {
      setAsking(false);
    }
  };
  const onKey = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } };
  const prompts = useMemo(() => ([
    'What documents are still missing?',
    'What did we last email them?',
    'What happened yesterday?',
    'Which lender approved this?',
    'Any open follow-ups?',
  ]), []);

  return (
    <div className="space-y-2" data-testid="onboarding-ghost-chat">
      {/* Ghost live-context strip — spec §2 "Ghost feels part of the CRM" */}
      <div
        className="text-[10.5px] text-indigo-900 bg-indigo-50 border border-indigo-100 rounded px-2 py-1 flex items-center gap-1.5 flex-wrap"
        data-testid="ghost-context-strip"
      >
        <Sparkles className="h-3 w-3 text-indigo-600 shrink-0" />
        <span>
          Ghost is live-connected to <span className="font-semibold">{clientName || 'this client'}</span>:{' '}
          <span className="text-indigo-700">deal · stage · timeline · emails · SMS · notes · documents · workflow · loan · team.</span>{' '}
          Ask naturally — no need to identify anyone.
        </span>
      </div>

      <div
        ref={scrollRef}
        className="rounded-md border border-slate-200 bg-white p-2 min-h-[13rem] max-h-[18rem] overflow-y-auto text-sm space-y-2"
        data-testid="ghost-chat-transcript"
      >
        {session.ghost.length === 0 && (
          <div className="text-[11px] text-slate-500 italic">
            Ask Ghost anything about this client. Type a question or use a prompt below.
          </div>
        )}
        {session.ghost.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'text-right' : 'text-left'}>
            <div className={
              m.role === 'user'
                ? 'inline-block bg-slate-900 text-white px-2 py-1 rounded-md text-xs max-w-[85%] whitespace-pre-wrap'
                : 'inline-block bg-indigo-50 border border-indigo-100 px-2 py-1 rounded-md text-xs max-w-[85%] whitespace-pre-wrap text-slate-900'
            }>
              {m.text}
            </div>
          </div>
        ))}
        {asking && <div className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Ghost is thinking…</div>}
      </div>

      <div className="flex items-center gap-1">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKey}
          placeholder="Ask Ghost anything about this client…"
          className="flex-1 text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-400"
          data-testid="ghost-chat-input"
        />
        <Button size="sm" onClick={() => ask()} disabled={!q.trim() || asking} data-testid="ghost-chat-send">
          {asking ? <Loader2 className="h-3 w-3 animate-spin" /> : <MessageCircle className="h-3 w-3" />}
        </Button>
      </div>
      {/* Optional prompts, de-emphasized */}
      <div className="flex flex-wrap gap-1" data-testid="ghost-suggestion-chips">
        <span className="text-[10px] text-slate-400 self-center pr-1">Optional:</span>
        {prompts.map((s) => (
          <button
            type="button"
            key={s}
            onClick={() => ask(s)}
            disabled={asking}
            className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 bg-white hover:bg-indigo-50 hover:border-indigo-300 text-slate-600 disabled:opacity-50"
            data-testid={`ghost-suggest-${s.slice(0, 10)}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
// Live Call Notes — high-visibility surface (spec §4)
// -------------------------------------------------------------------
function LiveNotes({ clientId, callSid, session, bumpSaved, updateDraft, onModalOpen }) {
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!onModalOpen) return;
    if (isTouchDevice()) return;
    const t = window.setTimeout(() => { textareaRef.current?.focus(); }, 60);
    return () => window.clearTimeout(t);
  }, [onModalOpen]);

  const save = async () => {
    const content = (session.draft || '').trim();
    if (!content) return;
    setSaving(true);
    try {
      const res = await api.post(`/clients/${clientId}/add-note`, {
        content, call_sid: callSid || null, source: 'onboarding_call_modal',
      });
      bumpSaved({ id: res.data?.activity?.id || `local-${Date.now()}`, content, at: new Date().toISOString() });
      updateDraft('');
      toast.success('Note saved to activity timeline');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };
  const onKey = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') save();
  };
  return (
    <div className="space-y-2" data-testid="onboarding-live-notes">
      {/* High-visibility notes surface — amber wash + thick border. */}
      <div className="rounded-md border-2 border-amber-300 bg-amber-50 p-2">
        <textarea
          ref={textareaRef}
          value={session.draft}
          onChange={(e) => updateDraft(e.target.value)}
          onKeyDown={onKey}
          placeholder="Type notes here while speaking — ⌘/Ctrl+Enter to save to the Activity Timeline"
          rows={5}
          className="w-full text-[13.5px] leading-snug rounded-sm border border-amber-400 bg-white px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 resize-none cursor-text placeholder:text-slate-500 placeholder:italic caret-amber-700 text-slate-900 font-medium"
          data-testid="onboarding-note-input"
        />
        <div className="flex items-center justify-between gap-2 mt-1.5">
          <div className="text-[11px] text-slate-700 truncate font-medium">
            {callSid
              ? <>Attached to call <span className="font-mono">{callSid.slice(-8)}</span></>
              : 'Standalone note · still saves to timeline'}
          </div>
          <Button
            onClick={save}
            disabled={!(session.draft || '').trim() || saving}
            className="h-10 px-4 bg-amber-600 hover:bg-amber-700 text-white font-semibold shadow-sm"
            data-testid="onboarding-save-note"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Send className="h-4 w-4 mr-1.5" />}
            Save note
          </Button>
        </div>
      </div>
      {session.saved.length > 0 && (
        <div className="pt-1" data-testid="onboarding-recent-notes">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1 flex items-center gap-1">
            <StickyNote className="h-3 w-3" /> Saved this call · {session.saved.length}
          </div>
          <div className="space-y-1 max-h-28 overflow-y-auto pr-1">
            {session.saved.map((r) => (
              <div key={r.id} className="text-xs text-slate-700 flex items-start gap-2">
                <CheckCircle2 className="h-3 w-3 mt-0.5 text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="whitespace-pre-wrap break-words">{r.content}</div>
                  <div className="text-[10px] text-slate-500">{relTime(r.at)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Inline Task Creator — reuses existing POST /tasks endpoint (spec §3)
// -------------------------------------------------------------------
function InlineTaskForm({ open, onClose, clientId, clientName, onCreated }) {
  const { user } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setTitle(''); setDescription(''); setDueDate(''); setDueTime('');
    }
  }, [open]);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await api.post('/tasks', {
        title:          title.trim(),
        description:    description.trim() || null,
        client_id:      clientId,
        due_date:       dueDate || null,
        due_time:       dueTime || null,
        external_email: null,
        assigned_users: [user?.id],
      });
      toast.success('Task created');
      onCreated?.();
      onClose();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Failed to create task');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="onboarding-task-form">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            <ListTodo className="h-4 w-4 text-emerald-700" /> Create Task
          </DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500">
            Filed against {clientName || 'this client'} · assigned to you.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-2">
          <input
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Task title"
            className="w-full text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="task-form-title"
          />
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            className="w-full text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400 resize-none"
            data-testid="task-form-description"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
              data-testid="task-form-due-date"
            />
            <input
              type="time"
              value={dueTime}
              onChange={(e) => setDueTime(e.target.value)}
              className="text-sm rounded-md border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
              data-testid="task-form-due-time"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button type="submit" size="sm" disabled={!title.trim() || saving} data-testid="task-form-submit">
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ListTodo className="h-3 w-3 mr-1" />}
              Create Task
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------------------------
// Modal shell
// -------------------------------------------------------------------
export default function OnboardingCallModal({ open, onOpenChange, clientId, callSid }) {
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openStamp, setOpenStamp] = useState(0);
  const [taskOpen, setTaskOpen] = useState(false);
  const [apptOpen, setApptOpen] = useState(false);

  const [, setBump] = useState(0);
  const session = getSession(clientId, callSid);
  const forceRerender = () => setBump((v) => v + 1);
  const updateDraft = (v)  => { session.draft = v; forceRerender(); };
  const bumpSaved   = (row) => { session.saved = [row, ...session.saved].slice(0, 25); forceRerender(); };
  const pushHistory = (row) => { session.ghost = [...session.ghost, row]; forceRerender(); };

  useEffect(() => {
    if (!open || !clientId) return;
    setOpenStamp((v) => v + 1);
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get(`/clients/${clientId}/onboarding-context`);
        if (!cancelled) setCtx(res.data);
      } catch (e) {
        if (!cancelled) setCtx(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, clientId]);

  const missingCount = ctx?.missing_documents?.length || 0;

  return (
    <>
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl p-0 gap-0 max-h-[92vh] overflow-y-auto"
        data-testid="onboarding-call-modal"
      >
        <DialogHeader className="px-4 pt-3 pb-2 border-b border-slate-200 sticky top-0 bg-white z-10">
          <DialogTitle className="flex items-center gap-2 text-base flex-wrap">
            <ClipboardList className="h-4 w-4 text-emerald-700" />
            <span>Onboarding Call</span>
            {ctx?.client_name && <span className="text-slate-500 font-normal">·</span>}
            {ctx?.client_name && <span className="font-semibold text-slate-900">{ctx.client_name}</span>}
            {ctx?.deal_stage_label && (
              <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[10px] font-semibold uppercase tracking-wider">
                {ctx.deal_stage_label}
              </span>
            )}
            {missingCount > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-semibold" data-testid="header-missing-docs-chip">
                {missingCount} missing doc{missingCount === 1 ? '' : 's'}
              </span>
            )}
            {session.saved.length > 0 && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-semibold" data-testid="header-saved-notes-chip">
                {session.saved.length} note{session.saved.length === 1 ? '' : 's'} saved
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-[11px] text-slate-500">
            Quick Reference · Ghost Assistant · Live Notes. State preserved for this call — close and reopen as needed.
          </DialogDescription>

          {/* Quick actions — spec §3 */}
          {clientId && (
            <div className="flex gap-2 mt-2" data-testid="onboarding-quick-actions">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800"
                onClick={() => setTaskOpen(true)}
                data-testid="onboarding-create-task-btn"
              >
                <ListTodo className="h-3.5 w-3.5 mr-1" /> Create Task
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 border-indigo-300 bg-indigo-50 hover:bg-indigo-100 text-indigo-800"
                onClick={() => setApptOpen(true)}
                data-testid="onboarding-create-appt-btn"
              >
                <CalendarPlus className="h-3.5 w-3.5 mr-1" /> Create Appointment
              </Button>
            </div>
          )}
        </DialogHeader>

        {/* Section order per spec: Quick Reference (left) · Ghost (right top) · Notes (right bottom).
            Mobile stacks in the same order (1 → 2 → 3). */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 p-3">
          <div
            className="order-1 md:order-1 md:row-span-2 border border-slate-200 rounded-md p-2.5 bg-white"
            data-testid="onboarding-section-quick-ref"
          >
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5 flex items-center gap-1">
              <ClipboardList className="h-3 w-3" /> Quick Reference
            </div>
            <QuickReference ctx={ctx} loading={loading} />
          </div>

          <div
            className="order-2 md:order-2 border border-indigo-200 rounded-md p-2.5 bg-white"
            data-testid="onboarding-section-ghost"
          >
            <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold mb-1.5 flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-indigo-500" /> Ghost Assistant
            </div>
            {clientId
              ? <GhostChat
                  clientId={clientId}
                  clientName={ctx?.client_name}
                  session={session}
                  pushHistory={pushHistory}
                />
              : <div className="text-xs text-slate-500">Waiting for client…</div>}
          </div>

          <div
            className="order-3 md:order-3 border-2 border-amber-200 rounded-md p-2.5 bg-white"
            data-testid="onboarding-section-notes"
          >
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-semibold mb-1.5 flex items-center gap-1">
              <StickyNote className="h-3 w-3" /> Live Call Notes
            </div>
            {clientId
              ? <LiveNotes
                  clientId={clientId}
                  callSid={callSid}
                  session={session}
                  bumpSaved={bumpSaved}
                  updateDraft={updateDraft}
                  onModalOpen={openStamp}
                />
              : <div className="text-xs text-slate-500">Waiting for client…</div>}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* Sibling modals — spec §3 "use existing CRM systems, no duplicate impl" */}
    <InlineTaskForm
      open={taskOpen}
      onClose={() => setTaskOpen(false)}
      clientId={clientId}
      clientName={ctx?.client_name}
      onCreated={() => { /* stay in workspace */ }}
    />
    {clientId && (
      <ScheduleAppointmentModal
        open={apptOpen}
        onOpenChange={setApptOpen}
        client={{ id: clientId, full_name: ctx?.client_name || '' }}
        currentUser={user}
        onScheduled={() => {
          toast.success('Appointment booked');
          setApptOpen(false);
          /* stay in workspace */
        }}
      />
    )}
    </>
  );
}
